import type { DropdownOption } from '@publishing/components/forms/dropdown/Dropdown';
import type { InputPreset, InputType } from '@publishing/components/forms/input/_types';
import type { SvgIconInput } from '@publishing/components/ui/Icon';
import { isInputPreset, VARIANT_PRESETS } from '@publishing/components/forms/input/_types';
import { LEAD_FORM_NAME, type LeadFormName } from '@/features/lead-form/constants/formName';
import type { ItemCategory } from '@/features/lead-form/utils/leadFormGtm';

// ── Submit action ─────────────────────────────────────────

export type SubmitAction =
  | { type: 'inline' }
  | { type: 'redirect'; href: string }
  | { type: 'link'; href: string; external?: boolean };

// ── Form field ────────────────────────────────────────────

export type FormFieldType = InputType | 'dropdown';
export type FormFieldExplicitType = Exclude<FormFieldType, InputPreset>;

interface FormFieldBase {
  name?: string;
  requiredMessage?: string;
  placeholder?: string;
  helperMessage?: string;
  size?: 'lg' | 'md' | 'sm';
  options?: DropdownOption[];
  validate?: (value: string, values: Record<string, string>) => string | null;
  defaultValue?: string;
  view?: boolean;
  /** true이면 모바일에서 옵션이 바텀시트로 열림 */
  isSheet?: boolean;
}

/** Input 프리셋 단축 — type에 'company' | 'phone' | 'email' 등을 쓰면 라벨·아이콘·required 자동 적용 */
export interface FormFieldPreset extends FormFieldBase {
  type: InputPreset;
  name?: string;
  label?: string;
  required?: boolean;
  icon?: SvgIconInput;
  comma?: boolean;
  inputOverride?: InputType;
}

/** 명시형 — name · label · type 모두 필수 */
export interface FormFieldExplicit extends FormFieldBase {
  name: string;
  label: string;
  type: FormFieldExplicitType;
  required?: boolean;
  icon?: SvgIconInput;
  comma?: boolean;
}

export type FormField = FormFieldPreset | FormFieldExplicit;

export function getFormFieldPresetKey(field: { type?: unknown }): InputPreset | undefined {
  if (typeof field.type === 'string' && isInputPreset(field.type)) return field.type as InputPreset;
  return undefined;
}

export function resolveField(field: FormField) {
  const presetKey = getFormFieldPresetKey(field);
  const preset = presetKey ? VARIANT_PRESETS[presetKey] : undefined;
  const inputOverride = preset && 'inputOverride' in field ? (field as FormFieldPreset).inputOverride : undefined;
  const resolvedType = preset ? (inputOverride ?? presetKey) : (field as FormFieldExplicit).type;

  return {
    ...field,
    name: field.name ?? preset?.name ?? '',
    type: resolvedType,
    label: field.label ?? preset?.label ?? '',
    icon: field.icon ?? preset?.icon,
    required: field.required ?? preset?.required,
    comma: field.comma ?? preset?.comma,
    helperMessage: field.helperMessage ?? preset?.helperMessage,
    view: field.view ?? false,
  } as const;
}

// ── Agreement ─────────────────────────────────────────────

export type FormAgreementLang = 'ko' | 'en';
export type FormAgreementConsent = 'optional' | 'required';

export interface FormAgreementOptions {
  marketing?: FormAgreementConsent;
  lang?: FormAgreementLang;
}

export function getRequiredAgreementIds(marketing: FormAgreementConsent): string[] {
  return marketing === 'required'
    ? ['isPrivacy', 'isMarketing']
    : ['isPrivacy'];
}

// ── Form menu (팝업 좌측 메뉴 / 다중 선택용) ──────────────

export const FORM_KEY = {
  SHARED_OFFICE:     'shared-office',
  PRIVATE_OFFICE:    'private-office',
  CUSTOM_OFFICE:     'custom-office',
  OFFICE_LEASE:      'office-lease',
  OFFICE_SOLUTION:   'office-solution',
  OFFICE_INTERIOR:   'office-interior',
  INDIVIDUAL_OFFICE: 'individual-office',
  LOUNGE_MEMBERSHIP: 'lounge-membership',
  // SPOT_TOTAL:       'spot-branch',
  PROMOTION:         'promotion',
  AFFILIATE:         'affiliate',
  PARTNERS:          'partners',
} as const;

export type FormKey = (typeof FORM_KEY)[keyof typeof FORM_KEY];

/** Button action / openPopup — LEAD_FORM_NAME 키('PROMOTION') 또는 값('N_프로모션') */
export type FormActionFormName = keyof typeof LEAD_FORM_NAME | LeadFormName;

export function resolveFormActionFormName(formName: FormActionFormName): LeadFormName {
  if (Object.prototype.hasOwnProperty.call(LEAD_FORM_NAME, formName)) {
    return LEAD_FORM_NAME[formName as keyof typeof LEAD_FORM_NAME];
  }
  return formName as LeadFormName;
}

export interface FormMenuConfig {
  key: FormKey;
  groupLabel?: string;
  label: string;
  /** SelectionList 카드 부가 설명 */
  selectionCaption?: string;
  fields?: FormField[];
  submitLabel?: string;
  /** 팝업 완료 화면 키 */
  completeKey?: string;
  agreement?: FormAgreementOptions;
  /** 스키마 productType 값 (e.g. 'SHARED_OFFICE'). 팝업 submit 시 API payload에 포함 */
  productTypeValue?: string;
  /**
   * GA `item_category`(사업 종류). 상품마다 속한 사업 라인이 달라 메뉴에 함께 둔다.
   * Salesforce 라우팅용 `systemInfo.leadType` 과는 별개다.
   */
  itemCategory?: ItemCategory;
}
